import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import HeaderWithToggleButton from './components/headerWithToggle';
import PlanDetails from './components/planDetails';
import {
  getFilterMonthlyAndYearly,
  upgradeSubscriptionEventPayload,
} from './components/helpers/upgradeSubscriptionHelper';
import LocaleKeys from '../../../locales/keys';
import {
  ELIGIBILITY_ACTION_EXTERNAL,
  ELIGIBILITY_ACTION_VIPOPS,
  ELIGIBILITY_ACTION_PURCHASE,
  MONTHLY,
  MONTHLY_TEXT,
  YEARLY,
  YEARLY_TEXT,
} from '../../../constants/upgradeJourney.constant';
import postMessage from '../../../utils/postMessage';
import CustomLink from './components/customLink';
const useStyles = makeStyles((theme) => ({
  upgradeSubscriptionContainer: {
    backgroundColor: '#f7f4f0',
    borderRadius: '5px',
  },
  saveButton: {
    width: '100%',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '20px',
    color: '#ffffff',
    '&:hover': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.dark} inset`,
      color: theme.palette.custom.black,
    },
    '&:disabled': {
      backgroundColor: theme.palette.custom.frame,
    },
    [theme.breakpoints.between('xs', 'md')]: {
      width: '100%',
      marginLeft: '0px',
    },
  },
  buttonNode: {
    textTransform: 'none',
  },
  IapText: {
    fontWeight: '600',
  },
}));

const UpgradeSubscription = ({
  eligibilityList,
  currentSubscription,
  subscriptionProps,
  language,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const [billCycle, setBillCycle] = useState(YEARLY);
  const [eligiblePlanCycle, setEligiblePlanCycle] = useState({});
  const [toggleBillCycleEligibleList, setToggleBillCycleEligibleList] =
    useState([]);
  const [selectedEligiblePlan, setSelectedEligiblePlan] = useState(null);
  const [disclaimerList, setDisclaimerList] = useState([]);
  const [headerLabel, setHeaderLabel] = useState(
    t(LocaleKeys.subscriptionInfo.subscriptions.upgradeSubscription),
  );
  const [buttonLabel, setButtonLabel] = useState(
    t(LocaleKeys.subscriptionInfo.subscriptions.upgrade),
  );
  const [toggleDisabled, setToggleDisabled] = useState({
    monthly: false,
    yearly: false,
  });

  const handleBillCycle = (_, newBillCycle) => {
    if (newBillCycle) {
      const billingCyclePayload =
        newBillCycle === 'monthly' ? MONTHLY_TEXT : YEARLY_TEXT;
      postMessage('module.subscriptionBillingCycleChange', {
        selection: billingCyclePayload,
      });
      setBillCycle(newBillCycle);
    }
  };

  useEffect(() => {
    if (eligibilityList) {
      const { EligiblePlans, EntitledPlans, Disclaimers } = eligibilityList;
      setDisclaimerList(Disclaimers?.items.length ? Disclaimers?.items : []);
      const findEntitledPlansWithCurrentSubscription = (
        EntitledPlans || []
      ).find(
        (entitledItem) =>
          Number(entitledItem?.subscriptionId) ===
          Number(currentSubscription?.Id),
      );
      const getSubscriptionInfoForUpgradePlans =
        findEntitledPlansWithCurrentSubscription?.subscriptionOptions;
      if (!getSubscriptionInfoForUpgradePlans) {
        setEligiblePlanCycle({});
        return;
      }
      const findCurrentSubscriptionFromEntitledPlans =
        getSubscriptionInfoForUpgradePlans?.reduce((all, item) => {
          const findSubscription = EligiblePlans?.filter(
            (eligiblePlansItem) => eligiblePlansItem.entitlement === item,
          );
          if (findSubscription.length) {
            all.push(...findSubscription);
          }
          return all;
        }, []);
      const filterValues = getFilterMonthlyAndYearly(
        findCurrentSubscriptionFromEntitledPlans,
      );
      setEligiblePlanCycle(filterValues);
      setToggleDisabled({
        monthly: !filterValues?.monthly?.length,
        yearly: !filterValues?.yearly?.length,
      });
      if (filterValues?.yearly?.length) {
        setBillCycle(YEARLY);
      } else {
        setBillCycle(MONTHLY);
      }
    }
  }, [eligibilityList]);

  useEffect(() => {
    if (eligiblePlanCycle?.[billCycle]) {
      const eligiblePlanCycleBasedOnSelect = eligiblePlanCycle[billCycle];
      eligiblePlanCycleBasedOnSelect.sort((a, b) => b?.Amount - a?.Amount);
      setToggleBillCycleEligibleList(eligiblePlanCycleBasedOnSelect);
      setSelectedEligiblePlan(eligiblePlanCycleBasedOnSelect[0]);
    }
  }, [eligiblePlanCycle, billCycle]);

  useEffect(() => {
    if (subscriptionProps) {
      if (subscriptionProps.eligibilityAction === ELIGIBILITY_ACTION_PURCHASE) {
        setHeaderLabel(t(LocaleKeys.subscriptionInfo.subscriptions.getPremium));
        setButtonLabel(t(LocaleKeys.subscriptionInfo.subscriptions.purchase));
      } else if (
        subscriptionProps.eligibilityAction?.includes(
          ELIGIBILITY_ACTION_EXTERNAL,
        )
      ) {
        setHeaderLabel(
          t(LocaleKeys.subscriptionInfo.subscriptions.manageSubscription),
        );
      } else {
        setHeaderLabel(
          t(LocaleKeys.subscriptionInfo.subscriptions.upgradeSubscription),
        );
        setButtonLabel(t(LocaleKeys.subscriptionInfo.subscriptions.upgrade));
      }
    }
  }, [subscriptionProps]);

  const formatDisclaimer = (text) => {
    const linkElements = ['[', ']', '(', ')'];
    let hasLink = linkElements.every((item) => text.includes(item));
    console.log(hasLink);
    if (hasLink) {
      const extlinkDesc = text.substr(
        text.indexOf('[') + 1,
        text.indexOf(']') - text.indexOf('[') - 1,
      );
      const extLinkUrl = text.substr(
        text.indexOf('(') + 1,
        text.indexOf(')') - text.indexOf('(') - 1,
      );
      text = text.replace(/\[(\w|\d|\s|\]|\(|:|\/|\.)*\)/, '');
      return (
        <>
          {text}
          <CustomLink href={extLinkUrl} target="_new">
            {extlinkDesc}
          </CustomLink>
        </>
      );
    }

    return text;
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      id="upgradeSubscriptionContainer"
    >
      {!!toggleBillCycleEligibleList?.length &&
        !subscriptionProps.eligibilityAction?.includes(
          ELIGIBILITY_ACTION_EXTERNAL,
        ) && (
          <Box>
            <Box
              display="flex"
              flexDirection="column"
              p={6}
              className={classes.upgradeSubscriptionContainer}
              gridRowGap={24}
            >
              <HeaderWithToggleButton
                billCycle={billCycle}
                handleBillCycle={handleBillCycle}
                headerLabel={headerLabel}
                toggleDisabled={toggleDisabled}
              />
              <PlanDetails
                toggleBillCycleEligibleList={toggleBillCycleEligibleList}
                setSelectedEligiblePlan={setSelectedEligiblePlan}
                language={language}
              />
              {disclaimerList.length
                ? disclaimerList.map((disclaimer, i) => (
                    <Box px={4} key={i}>
                      <Typography style={{ fontSize: '11px' }}>
                        {formatDisclaimer(disclaimer.description)}
                      </Typography>
                    </Box>
                  ))
                : null}
              <Box display="flex" justifyContent="center">
                <Button
                  disableRipple
                  disableFocusRipple
                  size="small"
                  className={classes.saveButton}
                  disabled={!selectedEligiblePlan}
                  onClick={() => {
                    const getEventPayload = upgradeSubscriptionEventPayload({
                      language,
                      selectedEligiblePlan,
                    });
                    if (
                      subscriptionProps.eligibilityAction ===
                      ELIGIBILITY_ACTION_PURCHASE
                    ) {
                      postMessage('module.subscriptionPurchase', {
                        ...getEventPayload,
                        ...selectedEligiblePlan,
                      });
                    } else {
                      postMessage('module.subscriptionUpgrade', {
                        ...getEventPayload,
                        ...selectedEligiblePlan,
                      });
                    }

                    history.push({
                      pathname: '/payment',
                      state: {
                        productExternalReference:
                          selectedEligiblePlan?.ProductProperties[
                            'Product reference'
                          ],
                        productExternalReferenceType: 'Product reference',
                        pricingPlanExternalReference:
                          selectedEligiblePlan?.PricingPlanProperties
                            ?.Subscription,
                        pathFrom:
                          subscriptionProps.eligibilityAction?.toLowerCase(),
                        activeSubscriptionDetails: { ...currentSubscription },
                        billCycle,
                        selectedEligiblePlan,
                      },
                    });
                  }}
                >
                  <Typography className={classes.buttonNode}>
                    {buttonLabel}
                  </Typography>
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      {subscriptionProps.eligibilityAction?.includes(
        ELIGIBILITY_ACTION_EXTERNAL,
      ) &&
        !subscriptionProps.eligibilityAction?.includes(
          ELIGIBILITY_ACTION_VIPOPS,
        ) && (
          <Box
            display="flex"
            flexDirection="column"
            p={6}
            className={classes.upgradeSubscriptionContainer}
            id="external"
            gridRowGap={24}
          >
            <HeaderWithToggleButton
              headerLabel={headerLabel}
              isExternal={true}
            />
            <Typography className={classes.IapText}>
              <Trans
                i18nKey={
                  LocaleKeys.subscriptionInfo.subscriptions[
                    subscriptionProps?.platformName
                      ?.replace(/\s/g, '')
                      .toLowerCase()
                  ]
                }
                components={{
                  CustomLink: <CustomLink />,
                }}
              />
            </Typography>
          </Box>
        )}
    </Box>
  );
};

export default UpgradeSubscription;
